/**
 * Anonymous guest sessions. Nobody signs up: the first visit silently creates a throwaway account
 * in PocketBase's `users` collection so API rules can tell players apart (admin rights, your own
 * vote, hidden votes). The SDK keeps the token in localStorage; the generated credentials are kept
 * too so an expired token can be renewed without losing your seat and admin rights.
 */
import { ClientResponseError } from "pocketbase";
import { pb } from "@/lib/pocketbase";

const STORAGE_KEY = "qpp_guest";

type Credentials = { email: string; password: string };

function readCredentials(): Credentials | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Credentials) : null;
  } catch {
    return null;
  }
}

/** A 4xx means "this session is gone", anything else (offline, 5xx) is worth surfacing. */
function isRejected(error: unknown): boolean {
  return error instanceof ClientResponseError && error.status >= 400 && error.status < 500;
}

async function signIn({ email, password }: Credentials): Promise<string> {
  const { record } = await pb.collection("users").authWithPassword(email, password);
  return record.id;
}

/** Resolves to the guest's user id, creating the guest on the first visit. */
export async function ensureGuest(): Promise<string> {
  if (pb.authStore.isValid) {
    try {
      const { record } = await pb.collection("users").authRefresh();
      return record.id;
    } catch (error) {
      if (!isRejected(error)) throw error;
    }
  }

  const saved = readCredentials();
  if (saved) {
    try {
      return await signIn(saved);
    } catch (error) {
      if (!isRejected(error)) throw error;
    }
  }

  const credentials = {
    email: `guest-${crypto.randomUUID()}@guest.invalid`,
    password: crypto.randomUUID(),
  };
  await pb.collection("users").create({ ...credentials, passwordConfirm: credentials.password });
  localStorage.setItem(STORAGE_KEY, JSON.stringify(credentials));
  return signIn(credentials);
}

const NAME_KEY = "qpp_name";

/** The display name used last time, to prefill the join form. */
export function lastPlayerName(): string {
  try {
    return localStorage.getItem(NAME_KEY) ?? "";
  } catch {
    return "";
  }
}

export function rememberPlayerName(name: string): void {
  try {
    localStorage.setItem(NAME_KEY, name);
  } catch {
    // Private mode: the name is just not prefilled next time.
  }
}
